import { AsyncPipe, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { GalleryDto } from '@nusign/picflow-backend-types';
import { firstValueFrom } from 'rxjs';
import { ColorPickerComponent } from '../color-picker/color-picker.component';
import { calcLegacyTheme } from '../gallery/lib/calc-legacy-theme';
import { StateService } from '../services/state.service';
import { StorageService } from '../services/storage.service';
import { TenantColorsService } from '../services/tenant-colors.service';
import { ButtonMenuItemComponent } from '../shared/components/button-menu-item/button-menu-item.component';
import { ClickTrackDirective } from '../shared/directives/click-track.directive';
import { TooltipDirective } from '../shared/directives/tooltip.directive';

@Component({
  selector: 'app-color-selector',
  templateUrl: './color-selector.component.html',
  styleUrls: ['./color-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle, ColorPickerComponent, ButtonMenuItemComponent, ClickTrackDirective, AsyncPipe, TooltipDirective],
})
export class ColorSelectorComponent implements OnChanges {
  private storage = inject(StorageService);
  private state = inject(StateService);
  private check = inject(ChangeDetectorRef);
  private tenantColorsService = inject(TenantColorsService);
  @Input() currentColor: string;
  @Input() gallery?: GalleryDto;
  @Input() allowOpacity = false;
  @Input() allowReset = false;
  @Output() currentColorChange = new EventEmitter<string>();
  previousColor: string;

  presetColors = [
    '#000000',
    '#5D6E82',
    '#CA0813',
    '#48818D',
    '#6751A5',
    '#A54F79',
    '#FFFFFF',
    '#BFCFE1',
    '#E49142',
    '#F0C143',
    '#407BD5',
  ];
  recentColors: string[];
  galleryColors = [];
  colorPickerOpen = false;

  brandColors$ = this.tenantColorsService.colors$;

  async ngOnChanges(changes: SimpleChanges) {
    if (Object.keys(changes).filter(key => key !== 'currentColor').length === 0 && this.recentColors) return;

    this.recentColors = [];
    this.galleryColors = [];

    const brandColors = await firstValueFrom(this.brandColors$);

    if (this.gallery) {
      const theme = this.gallery.theme || calcLegacyTheme(this.gallery);
      this.galleryColors = [
        theme.cover.title_typography.font_color,
        theme.cover.paragraph_typography.font_color,
        theme.section.title_typography.font_color,
        theme.section.paragraph_typography.font_color,
      ].filter(Boolean);
    }
    this.recentColors = this.getRecentColors();

    this.galleryColors = Array.from(new Set(this.galleryColors.filter(color => !this.presetColors.includes(color))));
    this.recentColors = this.recentColors.filter(
      color =>
        !this.presetColors.includes(color) &&
        !this.galleryColors.includes(color) &&
        !brandColors.some(c => c.color === color)
    );
    this.recentColors = this.recentColors.slice(0, 3);
    this.check.detectChanges();
  }

  selectColor(color: string) {
    this.currentColor = color;
    this.colorPickerChange();
  }

  colorPickerChange() {
    this.currentColorChange.emit(this.currentColor);
    this.saveRecentColors();
  }

  private saveRecentColors() {
    if (!this.currentColor) {
      this.previousColor = undefined;
      return;
    }
    const color = this.currentColor.slice(0, 7); // remove alpha
    if (!color) return;
    const recentColors = this.getRecentColors();
    const newRecentColors = recentColors.slice(0, 20).filter(c => c !== color && c !== this.previousColor);
    newRecentColors.unshift(color);
    this.storage.set(`recentColors_${this.getTenantId()}`, newRecentColors);

    this.previousColor = color;
  }

  customColorClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.colorPickerOpen = true;
  }

  private getTenantId() {
    return this.gallery?.['tenant']?.['id'] || this.gallery?.['tenant'] || this.state.activeTenant$?.value?.id;
  }

  private getRecentColors() {
    return this.storage.get<string[]>(`recentColors_${this.getTenantId()}`) || [];
  }
}
